import { Col, Row } from "antd";
import { get, isEmpty } from "lodash";
import { useTranslation } from "react-i18next";
import {
  MdOutlineCardMembership,
  MdOutlineChildCare,
  MdOutlineFamilyRestroom,
} from "react-icons/md";
import Button from "shared/components/Button";
import Form from "shared/components/Form";
import Input from "shared/components/Input";
import Label from "shared/components/Label";
import { NAME_SPACES } from "shared/locales/constants";
import { RELATIONSHIP } from "utils/constants";

const SocialSecurityCertificate = ({
  values,
  onFileRemove,
  setFieldValue,
  setPercent,
  subscription,
  children: childrenProp,
}) => {
  const { t } = useTranslation(NAME_SPACES.PRIVATE.BROKER.CONTRACT);
  const SUBSCRIPTION = t("SUBSCRIPTION", { returnObjects: true });

  // Get related persons from subscription
  const relatedPersons = get(
    subscription,
    "project.contact.relatedPersons",
    [],
  ).filter((_) => _);

  // Check for spouse
  const hasSpouse = !isEmpty(
    relatedPersons.find(
      ({ relationship }) => relationship === RELATIONSHIP.SPOUSE,
    ),
  );

  // Get children
  const children =
    childrenProp ||
    relatedPersons.filter(
      ({ relationship }) => relationship === RELATIONSHIP.CHILD,
    );
  const hasChildren = children.length > 0;

  const getChildTitle = (index) => {
    const titles =
      SUBSCRIPTION.FORM.PROOF.CHILDREN_ATTESTATION?.CHILD_TITLES || {};
    switch (index) {
      case 0:
        return titles.FIRST || "Premier enfant";
      case 1:
        return titles.SECOND || "Deuxième enfant";
      case 2:
        return titles.THIRD || "Troisième enfant";
      default:
        return `${index + 1}ème enfant`;
    }
  };

  return (
    <div className="subscription-form--wrapper">
      <Form
        type="vertical"
        title={SUBSCRIPTION.FORM.PROOF.SOCIAL_SECURITY_CERTIFICATE.TITLE}
        subtitle={SUBSCRIPTION.FORM.PROOF.SOCIAL_SECURITY_CERTIFICATE.SUBTITLE}
      >
        {/* Section 1: Adherent Attestation */}
        <Row gutter={[16, 16]}>
          <Col lg={24} xs={24}>
            <div className="subscription-form--group-label">
              <Label
                label={SUBSCRIPTION.FORM.PROOF.ADHERENT}
                icon={<MdOutlineCardMembership size={16} />}
                required
              />
            </div>
          </Col>
          <Col lg={24} xs={24}>
            <div className="subscription-form--custom-upload">
              <Input
                type="file"
                variant="uploader"
                validTypes={SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE}
                multiple
                onUpload={(fileList) => {
                  setFieldValue("proof.socialSecurityCertificate", fileList);
                  setPercent({
                    ...values,
                    proof: {
                      ...values.proof,
                      socialSecurityCertificate: fileList,
                    },
                  });
                }}
                onFileRemove={onFileRemove}
                fileList={get(values, "proof.socialSecurityCertificate", [])}
              >
                <Row align="center">
                  <Button type="primary--link">
                    {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                  </Button>
                </Row>
              </Input>
            </div>
          </Col>
        </Row>

        {/* Section 2: Spouse Attestation */}
        {hasSpouse && (
          <Row gutter={[16, 16]}>
            <Col lg={24} xs={24}>
              <div className="subscription-form--group-label">
                <Label
                  label={SUBSCRIPTION.FORM.PROOF.SPOUSE?.LABEL || "Conjoint(e)"}
                  icon={<MdOutlineFamilyRestroom size={16} />}
                  required
                />
              </div>
            </Col>
            <Col lg={24} xs={24}>
              <div className="subscription-form--custom-upload">
                <Input
                  type="file"
                  variant="uploader"
                  validTypes={SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE}
                  multiple
                  onUpload={(fileList) => {
                    setFieldValue(
                      "proof.spouseSocialSecurityCertificate",
                      fileList,
                    );
                    setPercent({
                      ...values,
                      proof: {
                        ...values.proof,
                        spouseSocialSecurityCertificate: fileList,
                      },
                    });
                  }}
                  onFileRemove={onFileRemove}
                  fileList={get(
                    values,
                    "proof.spouseSocialSecurityCertificate",
                    [],
                  )}
                >
                  <Row align="center">
                    <Button type="primary--link">
                      {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                    </Button>
                  </Row>
                </Input>
              </div>
            </Col>
          </Row>
        )}

        {/* Section 3: Children Attestations */}
        {hasChildren &&
          children.map((child, index) => {
            const userId = get(child, "identity.user.id");
            const firstName = get(child, "identity.user.firstname", "");
            const lastName = get(child, "identity.user.lastname", "");
            const childName = [firstName, lastName].filter(Boolean).join(" ");
            const title = getChildTitle(index);
            const titleWithName = childName ? `${title} - ${childName}` : title;

            const fieldKey = userId
              ? `childSocialSecurityCertificate_${userId}`
              : `childSocialSecurityCertificate_${index}`;

            return (
              <Row gutter={[16, 16]} key={userId || index}>
                <Col lg={24} xs={24}>
                  <div className="subscription-form--group-label">
                    <Label
                      label={titleWithName}
                      icon={<MdOutlineChildCare size={16} />}
                      required
                    />
                  </div>
                </Col>
                <Col lg={24} xs={24}>
                  <div className="subscription-form--custom-upload">
                    <Input
                      type="file"
                      variant="uploader"
                      validTypes={SUBSCRIPTION.FORM.PROOF.ALLOWED_FILES_TYPE}
                      multiple
                      onUpload={(fileList) => {
                        setFieldValue(`proof.${fieldKey}`, fileList);
                        setPercent({
                          ...values,
                          proof: {
                            ...values.proof,
                            [fieldKey]: fileList,
                          },
                        });
                      }}
                      onFileRemove={onFileRemove}
                      fileList={get(values, `proof.${fieldKey}`, [])}
                    >
                      <Row align="center">
                        <Button type="primary--link">
                          {SUBSCRIPTION.FORM.PROOF.ACTION.ADD}
                        </Button>
                      </Row>
                    </Input>
                  </div>
                </Col>
              </Row>
            );
          })}
      </Form>
    </div>
  );
};

export default SocialSecurityCertificate;
